import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGTMContext } from '../context/GTMContext';
import matchService from '../services/matchService';
import venueService from '../services/venueService';
import '../styles/match-checkout.css';

const MatchCheckout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const gtm = useGTMContext();
  const [match, setMatch] = useState(null);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState('');
  const [loading, setLoading] = useState(true);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Cargar datos del partido y las sedes disponibles
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Obtener datos del partido
        const matchData = await matchService.getMatchById(id);
        setMatch(matchData);
        
        // Si el partido ya tiene una sede asignada, seleccionarla por defecto
        if (matchData.venue && matchData.venue._id) {
          setSelectedVenue(matchData.venue._id);
        } else if (user && user.venue) {
          // Si el usuario tiene una sede preferida, seleccionarla por defecto
          setSelectedVenue(user.venue);
        }

        // Cargar sedes disponibles
        setVenuesLoading(true);
        const venuesData = await venueService.getActiveVenues();
        const activeVenues = Array.isArray(venuesData) 
          ? venuesData.filter((venue) => venue.active) 
          : venuesData?.venues?.filter((venue) => venue.active) || [];
        
        setVenues(activeVenues);
        setVenuesLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error al cargar los datos. Por favor, inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  // Manejar cambio de sede
  const handleVenueChange = (e) => {
    setSelectedVenue(e.target.value);
  };

  // Manejar cambio de método de pago
  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  // Procesar el pago y la inscripción
  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!selectedVenue) {
      setError('Por favor, selecciona una sede');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // En una implementación real, aquí procesaríamos el pago
      // y luego inscribiríamos al usuario en el partido

      // Simular procesamiento de pago
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Inscribir al usuario en el partido con la sede seleccionada
      await matchService.joinMatch(id, { venue: selectedVenue });

      setSuccess(true);

      // Enviar evento GTM de inscripción a partido
      gtm.sendCustomEvent('match_registration', {
        match_id: id,
        venue: selectedVenue,
        payment_method: paymentMethod,
        user_id: user?._id
      });

      // Redirigir al detalle del partido después de 2 segundos
      setTimeout(() => {
        navigate(`/match/${id}`);
      }, 2000);
    } catch (error) {
      console.error('Error processing checkout:', error);
      setError(
        (error.response && error.response.data && error.response.data.message) ||
          'Error al procesar el pago. Por favor, inténtalo de nuevo.',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando información del partido...</div>;
  }

  if (error && !match) {
    return <div className="error">{error}</div>;
  }

  if (!match) {
    return <div className="error">No se encontró el partido</div>;
  }

  const matchDate = new Date(match.date);

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Completar Inscripción</h1>
          <p>Estás a punto de inscribirte en un partido</p>
        </div>

        <div className="checkout-content">
          <div className="checkout-summary">
            <div className="match-summary-card">
              <h2>Resumen del Partido</h2>
              <div className="match-summary-content">
                <div className="match-summary-image">
                  <img
                    src={match.category.image || '/img/1x/icon-team.png'}
                    alt={match.category.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/img/1x/icon-team.png';
                    }}
                  />
                </div>
                <div className="match-summary-details">
                  <h3>{match.title || `Partido de ${match.category.name}`}</h3>
                  <div className="match-meta">
                    <div className="match-meta-item">
                      <span className="material-icons">event</span>
                      <span>
                        {matchDate.toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="match-meta-item">
                      <span className="material-icons">schedule</span>
                      <span>{match.startTime} - {match.endTime || '?'}</span>
                    </div>
                    <div className="match-meta-item">
                      <span className="material-icons">group</span>
                      <span>
                        {(match.players || []).length}/{match.maxPlayers} jugadores
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="price-summary-card">
              <h2>Resumen de Precio</h2>
              <div className="price-summary-content">
                <div className="price-item">
                  <span>Precio del partido</span>
                  <span>€{match.price.toFixed(2)}</span>
                </div>
                {match.discount > 0 && (
                  <div className="price-item discount">
                    <span>Descuento</span>
                    <span>-€{match.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="price-total">
                  <span>Total</span>
                  <span>€{(match.price - (match.discount || 0)).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="checkout-form-container">
            {success ? (
              <div className="checkout-success">
                <div className="success-icon">
                  <span className="material-icons">check_circle</span>
                </div>
                <h2>¡Inscripción Completada!</h2>
                <p>Tu inscripción al partido ha sido procesada correctamente.</p>
                <p>Redirigiendo al detalle del partido...</p>
              </div>
            ) : (
              <form className="checkout-form" onSubmit={handleCheckout}>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="form-section">
                  <h2>Selecciona una Sede</h2>
                  <div className="form-group">
                    <label htmlFor="venue">Sede</label>
                    <select
                      id="venue"
                      name="venue"
                      value={selectedVenue}
                      onChange={handleVenueChange}
                      className="form-control"
                      required
                      disabled={venuesLoading}
                    >
                      <option value="">Selecciona una sede</option>
                      {venues.map((venue) => (
                        <option key={venue._id || venue.id} value={venue._id || venue.id}>
                          {venue.name} - {venue.address.city}
                        </option>
                      ))}
                    </select>
                    {venuesLoading && <div className="form-loading">Cargando sedes...</div>}
                  </div>

                  {selectedVenue && venues.length > 0 && (
                    <div className="selected-venue-info">
                      {venues.filter((v) => (v._id || v.id) === selectedVenue).map((venue) => (
                        <div className="venue-card" key={venue._id || venue.id}>
                          <div className="venue-image">
                            <img 
                              src={venue.mainImage || '/img/default-venue.jpg'} 
                              alt={venue.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/img/default-venue.jpg';
                              }}
                            />
                          </div>
                          <div className="venue-details">
                            <h4>{venue.name}</h4>
                            <p>
                              <span className="material-icons">place</span>
                              {venue.address.street}, {venue.address.city}
                            </p>
                            <p>
                              <span className="material-icons">star</span>
                              {venue.rating || '4.5'} ({venue.reviewsCount || '0'} reseñas)
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <h2>Método de Pago</h2>
                  <div className="payment-methods">
                    <div className="payment-method">
                      <input
                        type="radio"
                        id="wallet"
                        name="paymentMethod"
                        value="wallet"
                        checked={paymentMethod === 'wallet'}
                        onChange={handlePaymentMethodChange}
                      />
                      <label htmlFor="wallet" className="payment-method-label">
                        <span className="payment-icon">
                          <span className="material-icons">account_balance_wallet</span>
                        </span>
                        <div className="payment-info">
                          <span className="payment-name">Monedero</span>
                          <span className="payment-balance">
                            Saldo: €{user?.walletBalance?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      </label>
                    </div>

                    <div className="payment-method">
                      <input
                        type="radio"
                        id="card"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={handlePaymentMethodChange}
                      />
                      <label htmlFor="card" className="payment-method-label">
                        <span className="payment-icon">
                          <span className="material-icons">credit_card</span>
                        </span>
                        <span className="payment-name">Tarjeta de Crédito/Débito o Bizum</span>
                      </label>
                    </div>

                    <div className="payment-method">
                      <input
                        type="radio"
                        id="paypal"
                        name="paymentMethod"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={handlePaymentMethodChange}
                      />
                      <label htmlFor="paypal" className="payment-method-label">
                        <span className="payment-icon">
                          <span className="material-icons">account_balance_wallet</span>
                        </span>
                        <span className="payment-name">PayPal</span>
                      </label>
                    </div>
                  </div>

                  {paymentMethod === 'card' && (
                    <div className="card-payment-form">
                      <div className="form-group">
                        <label htmlFor="cardNumber">Número de Tarjeta</label>
                        <input
                          type="text"
                          id="cardNumber"
                          placeholder="1234 5678 9012 3456"
                          className="form-control"
                          autoComplete="off"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="cardExpiry">Fecha de Expiración</label>
                          <input
                            type="text"
                            id="cardExpiry"
                            placeholder="MM/AA"
                            className="form-control"
                            autoComplete="off"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="cardCvc">CVC</label>
                          <input
                            type="text"
                            id="cardCvc"
                            placeholder="123"
                            className="form-control"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="cardName">Nombre en la Tarjeta</label>
                        <input
                          type="text"
                          id="cardName"
                          placeholder="NOMBRE APELLIDO"
                          className="form-control"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'paypal' && (
                    <div className="paypal-payment-info">
                      <p>Serás redirigido a PayPal para completar el pago después de hacer clic en &quot;Completar Inscripción&quot;.</p>
                    </div>
                  )}
                </div>

                <div className="checkout-actions">
                  <Link to={`/match/${id}`} className="btn btn-secondary">
                    Cancelar
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isProcessing || !selectedVenue}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner"></span>
                        Procesando...
                      </>
                    ) : (
                      'Completar Inscripción'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchCheckout;
